import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GalleryItem, imgSrc } from "../lib/gallery";
import { lockScroll } from "../lib/scroll";

export function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
  onCustomise,
}: {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onNavigate: (dir: 1 | -1) => void;
  onCustomise: (item: GalleryItem) => void;
}) {
  const item = items[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNavigate(1);
      else if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", onKey);
    lockScroll(true);
    return () => {
      window.removeEventListener("keydown", onKey);
      lockScroll(false);
    };
  }, [onClose, onNavigate]);

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-charcoal/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      role="dialog"
      aria-modal="true"
      aria-label={`${item.title} — design preview`}
      onClick={onClose}
      data-testid="lightbox"
    >
      <motion.div
        key={item.id}
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg bg-ivory rounded-3xl overflow-hidden shadow-tuft-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imgSrc(item.img, 1200)}
          alt={item.alt}
          width={1024}
          height={1024}
          className="w-full aspect-square object-cover"
          data-testid="lightbox-image"
        />
        <button
          onClick={onClose}
          aria-label="Close preview"
          data-testid="lightbox-close-btn"
          className="absolute top-3 right-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-ivory/90 text-forest shadow-tuft transition-transform duration-200 hover:scale-105"
        >
          <X className="h-5 w-5" />
        </button>
        <button
          onClick={() => onNavigate(-1)}
          aria-label="Previous design"
          data-testid="lightbox-prev-button"
          className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-ivory/90 text-forest shadow-tuft transition-transform duration-200 hover:scale-105"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={() => onNavigate(1)}
          aria-label="Next design"
          data-testid="lightbox-next-button"
          className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-ivory/90 text-forest shadow-tuft transition-transform duration-200 hover:scale-105"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-serif text-2xl text-forest leading-tight">{item.title}</p>
              <p className="text-sm text-charcoal/60 mt-0.5">
                {item.direction} · {item.category}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-brass-soft border border-brass/50 text-forest text-[11px] font-medium px-3 py-1.5">
              {item.badge === "concept" ? "Concept render" : "Reference photo"}
            </span>
          </div>
          {item.badge === "concept" && (
            <p className="mt-3 text-xs leading-relaxed text-charcoal/60">
              Original concept visualisation, made to order. Fan-inspired artwork is not affiliated
              with, endorsed by, or licensed by any rights holder.
            </p>
          )}
          <button
            onClick={() => onCustomise(item)}
            data-testid="lightbox-customise-button"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest text-ivory font-medium px-6 py-3 transition-all duration-200 hover:bg-forest-deep hover:-translate-y-0.5"
          >
            Customise This Design
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
